/**
 * The async calls that make up a chat deletion. The dbApi `deleteThread` is the
 * authoritative delete; the cortevaChain folder/thread and metrics-redaction
 * calls are best-effort cleanup.
 */
export interface DeleteThreadDeps {
  deleteLegacyFolder: () => Promise<unknown>;
  deleteChatThread: () => Promise<unknown>;
  redactChatRecord: () => Promise<unknown>;
  /** Authoritative delete (dbApi mutateAsync); its onSuccess evicts cache. */
  deleteThread: () => Promise<unknown>;
}

/**
 * Deletes all resources for a chat. The dbApi delete is authoritative (its
 * mutation `onSuccess` evicts the thread's threads/messages/tasks from the
 * React Query cache); the cortevaChain and metrics calls are best-effort.
 *
 * Resolves only once every call has settled, so a caller awaiting this is
 * guaranteed the cache eviction has run before it navigates. Rejects iff the
 * authoritative delete fails; best-effort failures are swallowed.
 */
export async function deleteThreadResources(
  deps: DeleteThreadDeps
): Promise<void> {
  const dbDelete = deps.deleteThread();
  await Promise.allSettled([
    deps.deleteLegacyFolder(),
    deps.deleteChatThread(),
    deps.redactChatRecord(),
    dbDelete,
  ]);
  // Re-throw if the authoritative delete rejected; the best-effort calls above
  // may fail without failing the whole deletion.
  await dbDelete;
}
